
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form';
import { PartnerSelector } from './PartnerSelector';

import { merchantApi } from '../lib/api/merchantApi';
import { CreateMerchantAdminRequest, PartnerDto } from '../lib/api/types';

// Validation schema for admin merchant creation
const createMerchantAdminSchema = z.object({
  MerchantName: z
    .string()
    .min(1, 'Merchant name is required')
    .max(100, 'Merchant name must be 100 characters or less'),
  AccountNumber: z
    .string()
    .min(1, 'Account number is required')
    .max(50, 'Account number must be 50 characters or less')
    .regex(/^[0-9]+$/, 'Account number must contain only digits'),
  PartnerId: z
    .string()
    .min(1, 'Partner selection is required'),
});

type CreateMerchantAdminFormData = z.infer<typeof createMerchantAdminSchema>;

interface CreateMerchantAdminFormProps {
  onSuccess?: (merchantId: string) => void;
}

export const CreateMerchantAdminForm: React.FC<CreateMerchantAdminFormProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedPartner, setSelectedPartner] = React.useState<PartnerDto | null>(null);

  const form = useForm<CreateMerchantAdminFormData>({
    resolver: zodResolver(createMerchantAdminSchema),
    defaultValues: {
      MerchantName: '',
      AccountNumber: '',
      PartnerId: '',
    },
  });

  const createMerchantAdminMutation = useMutation({
    mutationFn: (data: CreateMerchantAdminRequest) => merchantApi.createMerchantAdmin(data),
    onSuccess: (merchantId) => {
      toast.success('Merchant created successfully', {
        description: `Merchant ID: ${merchantId}`,
      });
      form.reset();
      setSelectedPartner(null);
      onSuccess?.(merchantId);
      queryClient.invalidateQueries({ queryKey: ['merchants'] });
      // Navigate to merchant list page
      navigate('/merchants');
    },
    onError: (error: any) => {
      console.error('Error creating merchant:', error);
      let errorMessage = 'Failed to create merchant';

      if (error.response?.status === 409) {
        errorMessage = 'Merchant already exists with this account number';
      } else if (error.response?.data?.ErrorMessage) {
        errorMessage = error.response.data.ErrorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error('Failed to create merchant', {
        description: errorMessage,
      });
    },
  });

  const onSubmit = (data: CreateMerchantAdminFormData) => {
    if (!selectedPartner) {
      toast.error('Please select a partner');
      return;
    }

    createMerchantAdminMutation.mutate({
      MerchantName: data.MerchantName,
      AccountNumber: data.AccountNumber,
      PartnerId: data.PartnerId,
    });
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Create Merchant (Admin)</CardTitle>
        <CardDescription>
          Create a new merchant using the simplified admin interface
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Merchant Name */}
            <FormField
              control={form.control}
              name="MerchantName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Merchant Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter merchant name"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    The name of the merchant (1-100 characters)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Account Number */}
            <FormField
              control={form.control}
              name="AccountNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Number</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter account number"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    The merchant's account number (numeric only, 1-50 characters)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Partner Selection */}
             <FormField
               control={form.control}
               name="PartnerId"
               render={({ field }) => (
                 <FormItem>
                   <FormLabel>Partner</FormLabel>
                   <FormControl>
                     <PartnerSelector
                       selectedPartnerId={field.value}
                       onPartnerSelect={(partner) => {
                         field.onChange(partner?.partnerId || '');
                         setSelectedPartner(partner);
                       }}
                       placeholder="Select a partner"
                       required
                     />
                   </FormControl>
                   <FormDescription>
                     Select the partner for this merchant
                   </FormDescription>
                   <FormMessage />
                 </FormItem>
               )}
             />



            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              disabled={createMerchantAdminMutation.isPending}
            >
              {createMerchantAdminMutation.isPending ? 'Creating...' : 'Create Merchant'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
